import { useState, useCallback } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  BrainCircuit, TrendingUp, PiggyBank, AlertTriangle,
  Lightbulb, Loader2, CheckCircle2, Star, Briefcase,
  ChevronDown, ChevronUp, ArrowRight, RefreshCw, ScanLine, GraduationCap,
  CircleDollarSign, Target, Home, BookOpen, BarChart2, DollarSign, Users, FileText, Wallet
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { fmt, API_URL, gradeConfig } from './constants';

function TaxReturnResults({ taxData, onReset, onAnalyze, analysis, analyzing, analysisError }) {
  const [formsExpanded, setFormsExpanded] = useState(false);

  if (!taxData || Object.keys(taxData).length === 0) return null;

  const income = taxData.income || {};
  const deductions = taxData.deductions || {};
  const scheduleC = taxData.schedule_c;
  const formDetails = taxData.form_details || {};

  const incomeRows = [
    { label: 'W-2 Wages', value: income.wages },
    { label: 'Interest Income', value: income.interest },
    { label: 'Dividend Income', value: income.dividends },
    { label: 'Business Income (Sch C)', value: income.business },
    { label: 'Capital Gains/Losses', value: income.capital_gains },
    ...(income.other || []).map(o => ({ label: typeof o === 'object' ? (o.description || 'Other') : 'Other', value: typeof o === 'object' ? o.amount : o })),
  ].filter(r => r.value != null);

  const creditsList = taxData.credits || [];
  const formsList = taxData.forms_present || [];
  const retirementList = taxData.retirement_contributions || [];

  // Form icon mapping
  const formIcon = (name) => {
    const n = (name || '').toLowerCase();
    if (n.includes('k-1') || n.includes('k1')) return Wallet;
    if (n.includes('1065') || n.includes('partnership')) return Users;
    if (n.includes('schedule c') || n.includes('sch c')) return Briefcase;
    if (n.includes('schedule a') || n.includes('sch a')) return PiggyBank;
    if (n.includes('1040') || n.includes('individual')) return Home;
    if (n.includes('w-2') || n.includes('w2')) return DollarSign;
    if (n.includes('1099')) return CircleDollarSign;
    if (n.includes('8825') || n.includes('rental')) return Home;
    if (n.includes('balance sheet') || n.includes('schedule l')) return BarChart2;
    return FileText;
  };

  // Helper to render form detail values
  const renderFormValue = (key, val) => {
    if (val == null) return null;
    if (typeof val === 'object' && !Array.isArray(val)) {
      return Object.entries(val).filter(([, v]) => v != null).map(([k, v]) => (
        <div key={k} className="flex items-center justify-between text-[11px] py-0.5">
          <span className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}</span>
          <span className="font-mono text-muted-foreground">{typeof v === 'number' ? fmt(v) : String(v)}</span>
        </div>
      ));
    }
    if (Array.isArray(val)) {
      return val.length > 0 ? <span className="text-[11px] text-muted-foreground">{val.map(v => typeof v === 'object' ? (v.name || v.description || JSON.stringify(v)) : v).join(', ')}</span> : null;
    }
    return (
      <div className="flex items-center justify-between text-[11px] py-0.5">
        <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
        <span className="font-mono text-muted-foreground">{typeof val === 'number' ? fmt(val) : String(val)}</span>
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="tax-return-results">
      {/* Header */}
      <Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-50/30 dark:bg-emerald-900/10">
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="font-semibold text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Tax Return Extracted Successfully
                </p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {taxData.tax_year && <Badge variant="outline" className="text-[10px] bg-muted">{taxData.tax_year} Tax Year</Badge>}
                  {taxData.filing_status && <Badge variant="outline" className="text-[10px] bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 border-0">{taxData.filing_status}</Badge>}
                  {formsList.length > 0 && <Badge variant="outline" className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-0">{formsList.length} Forms Detected</Badge>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={onAnalyze} disabled={analyzing}
                className="bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white gap-1.5 shadow-sm"
                data-testid="analyze-tax-btn">
                {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BrainCircuit className="w-3.5 h-3.5" />}
                {analyzing ? 'Analyzing...' : (analysis ? 'Re-Analyze' : 'Analyze with CFO Buck')}
              </Button>
              <Button size="sm" variant="outline" onClick={onReset} data-testid="scan-another-tax-btn">
                <ScanLine className="w-3.5 h-3.5 mr-1" /> Scan Another
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Analyzing Progress Indicator */}
      {analyzing && (
        <Card className="border-violet-200 dark:border-violet-800 bg-violet-50/30 dark:bg-violet-900/10 animate-pulse">
          <CardContent className="p-4 flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-violet-500 animate-spin flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-violet-700 dark:text-violet-300">Analyzing your tax return...</p>
              <p className="text-xs text-violet-500 dark:text-violet-400 mt-0.5">CFO Buck is cross-referencing your return with the Tax Playbook. This may take 30-60 seconds.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Forms Detected - Expandable Detail Cards */}
      {formsList.length > 0 && (
        <div>
          <button
            onClick={() => setFormsExpanded(!formsExpanded)}
            className="flex items-center gap-2 w-full text-left mb-2 group"
            data-testid="toggle-form-details"
          >
            <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Forms Detected ({formsList.length})</span>
            {Object.keys(formDetails).length > 0 && (
              <>
                <span className="text-[10px] text-violet-500 font-medium">— Click to {formsExpanded ? 'collapse' : 'view details'}</span>
                {formsExpanded ? <ChevronUp className="w-3 h-3 text-muted-foreground" /> : <ChevronDown className="w-3 h-3 text-muted-foreground" />}
              </>
            )}
          </button>

          {!formsExpanded ? (
            <div className="flex flex-wrap gap-1.5">
              {formsList.map((f, i) => {
                const Icon = formIcon(f);
                return (
                  <Badge key={i} variant="outline" className="text-[10px] px-2 py-0.5 bg-muted border-border gap-1">
                    <Icon className="w-3 h-3 text-muted-foreground" />{f}
                  </Badge>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="form-detail-cards">
              {formsList.map((f, i) => {
                const Icon = formIcon(f);
                const detail = formDetails[f];
                const detailData = detail?.data || {};
                const hasData = Object.keys(detailData).length > 0;
                return (
                  <Card key={i} className="border-border hover:border-primary/30 dark:hover:border-primary/30 transition-colors">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                          <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-foreground truncate">{f}</p>
                          {detail?.page && <p className="text-[10px] text-muted-foreground">Page {detail.page}</p>}
                        </div>
                      </div>
                      {hasData ? (
                        <div className="space-y-0.5 border-t border-border pt-2">
                          {Object.entries(detailData).slice(0, 6).map(([key, val]) => (
                            <div key={key}>{renderFormValue(key, val)}</div>
                          ))}
                          {Object.keys(detailData).length > 6 && (
                            <p className="text-[10px] text-muted-foreground pt-1">+{Object.keys(detailData).length - 6} more fields</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-[10px] text-muted-foreground italic">No extractable data on this form</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Income Breakdown */}
        <Card className="border-border">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Income</span>
            </div>
            {incomeRows.length > 0 ? (
              <div className="space-y-2.5">
                {incomeRows.map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                    <span className="text-sm text-muted-foreground">{row.label}</span>
                    <span className="text-sm font-semibold font-mono text-foreground">{fmt(row.value)}</span>
                  </div>
                ))}
                {(income.total_gross || income.agi) && (
                  <div className="pt-2 border-t-2 border-border space-y-1.5">
                    {income.total_gross != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-foreground">Total Gross Income</span>
                        <span className="text-base font-bold font-mono text-foreground">{fmt(income.total_gross)}</span>
                      </div>
                    )}
                    {income.agi != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">Adjusted Gross Income (AGI)</span>
                        <span className="text-base font-bold font-mono text-emerald-600 dark:text-emerald-400">{fmt(income.agi)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-4">No income data extracted</p>
            )}
          </CardContent>
        </Card>

        {/* Deductions & Tax */}
        <Card className="border-border">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <PiggyBank className="w-4 h-4 text-violet-500" />
              <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Deductions & Tax</span>
            </div>
            <div className="space-y-2.5">
              {deductions.type && (
                <div className="flex items-center justify-between py-1.5 border-b border-border">
                  <span className="text-sm text-muted-foreground">
                    Deduction Type
                  </span>
                  <Badge variant="outline" className={cn("text-[10px] border-0",
                    deductions.type === 'itemized' ? "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400" : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                  )}>{deductions.type === 'itemized' ? 'Itemized' : 'Standard'}</Badge>
                </div>
              )}
              {deductions.amount != null && (
                <div className="flex items-center justify-between py-1.5 border-b border-border">
                  <span className="text-sm text-muted-foreground">Total Deductions</span>
                  <span className="text-sm font-semibold font-mono text-violet-600 dark:text-violet-400">{fmt(deductions.amount)}</span>
                </div>
              )}
              {/* Itemized breakdown */}
              {deductions.type === 'itemized' && deductions.itemized_breakdown && Object.keys(deductions.itemized_breakdown).length > 0 && (
                <div className="pl-3 border-l-2 border-violet-200 dark:border-violet-800 space-y-1.5 my-2">
                  {Object.entries(deductions.itemized_breakdown).map(([key, val]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="text-xs font-mono text-muted-foreground">{fmt(val)}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Above the line deductions */}
              {deductions.above_the_line?.length > 0 && (
                <div className="pt-1">
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1 block">Above-the-Line</span>
                  <div className="space-y-1">
                    {deductions.above_the_line.map((d, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">{typeof d === 'object' ? d.description : d}</span>
                        {typeof d === 'object' && d.amount != null && <span className="text-xs font-mono text-muted-foreground">{fmt(d.amount)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {taxData.taxable_income != null && (
                <div className="flex items-center justify-between py-1.5 border-b border-border">
                  <span className="text-sm text-muted-foreground">Taxable Income</span>
                  <span className="text-sm font-semibold font-mono text-foreground">{fmt(taxData.taxable_income)}</span>
                </div>
              )}
              {taxData.tax_liability != null && (
                <div className="flex items-center justify-between py-1.5 border-b border-border">
                  <span className="text-sm text-muted-foreground">Total Tax Liability</span>
                  <span className="text-sm font-semibold font-mono text-rose-600 dark:text-rose-400">{fmt(taxData.tax_liability)}</span>
                </div>
              )}
              {taxData.payments_withholdings != null && (
                <div className="flex items-center justify-between py-1.5 border-b border-border">
                  <span className="text-sm text-muted-foreground">Payments & Withholdings</span>
                  <span className="text-sm font-semibold font-mono text-blue-600 dark:text-blue-400">{fmt(taxData.payments_withholdings)}</span>
                </div>
              )}
              {taxData.refund_or_owed != null && (
                <div className="pt-2 border-t-2 border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground">
                      {taxData.refund_or_owed >= 0 ? 'Refund' : 'Amount Owed'}
                    </span>
                    <span className={cn("text-lg font-bold font-mono",
                      taxData.refund_or_owed >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"
                    )}>
                      {taxData.refund_or_owed >= 0 ? '+' : ''}{fmt(taxData.refund_or_owed)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Schedule C, Credits, Retirement - Secondary Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Credits */}
        {creditsList.length > 0 && (
          <Card className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-4 h-4 text-amber-500" />
                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Credits Claimed</span>
              </div>
              <div className="space-y-2">
                {creditsList.map((c, i) => (
                  <div key={i} className="flex items-center justify-between py-1 border-b border-border last:border-0">
                    <span className="text-xs text-muted-foreground">{typeof c === 'object' ? c.name : c}</span>
                    {typeof c === 'object' && c.amount != null && (
                      <span className="text-xs font-mono font-semibold text-amber-600 dark:text-amber-400">{fmt(c.amount)}</span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Schedule C */}
        {scheduleC && (
          <Card className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Briefcase className="w-4 h-4 text-blue-500" />
                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Schedule C</span>
              </div>
              <div className="space-y-2">
                {scheduleC.business_name && (
                  <div className="flex items-center justify-between py-1 border-b border-border">
                    <span className="text-xs text-muted-foreground">Business</span>
                    <span className="text-xs font-medium text-foreground">{scheduleC.business_name}</span>
                  </div>
                )}
                {scheduleC.gross_income != null && (
                  <div className="flex items-center justify-between py-1 border-b border-border">
                    <span className="text-xs text-muted-foreground">Gross Income</span>
                    <span className="text-xs font-mono font-semibold text-emerald-600">{fmt(scheduleC.gross_income)}</span>
                  </div>
                )}
                {scheduleC.net_profit != null && (
                  <div className="flex items-center justify-between py-1">
                    <span className="text-xs text-muted-foreground">Net Profit</span>
                    <span className={cn("text-xs font-mono font-semibold", scheduleC.net_profit >= 0 ? "text-emerald-600" : "text-rose-600")}>
                      {fmt(scheduleC.net_profit)}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Retirement Contributions */}
        {retirementList.length > 0 && (
          <Card className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <GraduationCap className="w-4 h-4 text-emerald-500" />
                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Retirement</span>
              </div>
              <div className="space-y-2">
                {retirementList.map((r, i) => (
                  <div key={i} className="flex items-center justify-between py-1 border-b border-border last:border-0">
                    <span className="text-xs text-muted-foreground">{typeof r === 'object' ? r.type : r}</span>
                    {typeof r === 'object' && r.amount != null && (
                      <span className="text-xs font-mono font-semibold text-emerald-600 dark:text-emerald-400">{fmt(r.amount)}</span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* CTA: Analyze or Analysis Results */}
      {analysisError && (
        <Card className="border-rose-200 dark:border-rose-800 bg-rose-50/50 dark:bg-rose-900/10">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-rose-700 dark:text-rose-400">Analysis Failed</p>
              <p className="text-xs text-rose-600 dark:text-rose-400/80 mt-0.5">{analysisError}</p>
            </div>
            <Button size="sm" variant="outline" onClick={onAnalyze}
              className="border-rose-200 text-rose-600 hover:bg-rose-100 flex-shrink-0"
              data-testid="retry-analysis-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry
            </Button>
          </CardContent>
        </Card>
      )}
      {!analysis && !analysisError ? (
        <Card className="border-violet-200 dark:border-violet-800 bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-900/10 dark:to-indigo-900/10">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/20">
              <Lightbulb className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Ready to find missed savings?
              </p>
              <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-0.5">
                CFO Buck will analyze your tax return against the Tax Playbook, identify missed deductions and credits, and generate a year-ahead tax plan.
              </p>
            </div>
            <Button onClick={onAnalyze} disabled={analyzing}
              className="bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white gap-1.5 shadow-sm flex-shrink-0"
              data-testid="analyze-tax-cta-btn">
              {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <BrainCircuit className="w-4 h-4" />}
              {analyzing ? 'Analyzing...' : 'Analyze Now'}
            </Button>
          </CardContent>
        </Card>
      ) : analysis ? (
        <TaxAnalysisReport analysis={analysis} />
      ) : null}
    </div>
  );
}

// ─── TAX ANALYSIS REPORT ─────────────────────────────────────────────────────
function TaxAnalysisReport({ analysis }) {
  const [expandedSection, setExpandedSection] = useState('summary');
  if (!analysis) return null;

  const { summary, form_summaries, missed_deductions, missed_credits, filing_optimization, playbook_strategies, tax_plan, red_flags } = analysis;
  const applicableStrategies = (playbook_strategies || []).filter(s => s.applicable).sort((a, b) => (a.priority || 99) - (b.priority || 99));
  const totalPlanSavings = tax_plan?.estimated_total_savings || applicableStrategies.reduce((s, x) => s + (x.estimated_savings || 0), 0);

  const Section = ({ id, title, icon: Icon, iconColor, count, children }) => {
    const isOpen = expandedSection === id;
    return (
      <Card className="border-border overflow-hidden">
        <button
          onClick={() => setExpandedSection(isOpen ? null : id)}
          className="w-full flex items-center justify-between p-4 hover:bg-muted transition-colors"
          data-testid={`analysis-section-${id}`}
        >
          <div className="flex items-center gap-3">
            <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", iconColor)}>
              <Icon className="w-4 h-4" />
            </div>
            <span className="text-sm font-semibold text-foreground">{title}</span>
            {count > 0 && <Badge variant="outline" className="text-[10px] ml-1">{count}</Badge>}
          </div>
          {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </button>
        {isOpen && <div className="border-t border-border p-4">{children}</div>}
      </Card>
    );
  };

  const priorityColor = (p) => p <= 2 ? 'text-rose-600 bg-rose-50' : p <= 3 ? 'text-amber-600 bg-amber-50' : 'text-muted-foreground bg-muted';
  const severityColor = (s) => s === 'high' ? 'text-rose-600 bg-rose-50 border-rose-200' : s === 'medium' ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-blue-600 bg-blue-50 border-blue-200';

  return (
    <div className="space-y-4" data-testid="tax-analysis-report">
      {/* Summary Banner */}
      {summary && (
        <Card className="border-violet-200 dark:border-violet-800 bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-900/10 dark:to-indigo-900/10">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/20">
                <BrainCircuit className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-lg font-bold text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Tax Analysis Report
                </p>
                <p className="text-sm text-muted-foreground mt-1">{summary.headline}</p>
                {summary.key_finding && (
                  <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-card/60 border border-violet-100 dark:border-violet-800">
                    <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-muted-foreground">{summary.key_finding}</p>
                  </div>
                )}
                <div className="flex items-center gap-4 mt-3 flex-wrap">
                  {summary.effective_tax_rate && (
                    <div className="text-xs"><span className="text-muted-foreground">Effective Rate: </span><span className="font-semibold text-foreground">{summary.effective_tax_rate}</span></div>
                  )}
                  {totalPlanSavings > 0 && (
                    <div className="text-xs"><span className="text-muted-foreground">Potential Savings: </span><span className="font-bold text-emerald-600">{fmt(totalPlanSavings)}</span></div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Form Summaries */}
      {form_summaries?.length > 0 && (
        <Section id="forms" title="Form Summaries" icon={FileText} iconColor="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" count={form_summaries.length}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {form_summaries.map((fs, i) => (
              <div key={i} className="p-3 rounded-lg border border-border bg-muted/50">
                <p className="text-xs font-semibold text-foreground">{fs.form_name}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{fs.description}</p>
                {fs.key_figures && Object.keys(fs.key_figures).length > 0 && (
                  <div className="mt-2 space-y-0.5">
                    {Object.entries(fs.key_figures).map(([k, v]) => (
                      <div key={k} className="flex justify-between text-[11px]">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="font-mono text-muted-foreground">{v}</span>
                      </div>
                    ))}
                  </div>
                )}
                {fs.notes && <p className="text-[10px] text-muted-foreground mt-2 italic">{fs.notes}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Missed Deductions */}
      {missed_deductions?.length > 0 && (
        <Section id="deductions" title="Missed Deductions" icon={PiggyBank} iconColor="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400" count={missed_deductions.length}>
          <div className="space-y-3">
            {missed_deductions.map((d, i) => (
              <div key={i} className="p-3 rounded-lg border border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-foreground">{d.deduction}</span>
                  {d.estimated_savings > 0 && <span className="text-sm font-bold text-emerald-600">{fmt(d.estimated_savings)}</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{d.explanation}</p>
                {d.action && (
                  <div className="flex items-start gap-1.5 mt-2">
                    <ArrowRight className="w-3 h-3 text-violet-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-violet-600 dark:text-violet-400 font-medium">{d.action}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Missed Credits */}
      {missed_credits?.length > 0 && (
        <Section id="credits" title="Missed Credits" icon={Star} iconColor="bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400" count={missed_credits.length}>
          <div className="space-y-3">
            {missed_credits.map((c, i) => (
              <div key={i} className="p-3 rounded-lg border border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-foreground">{c.credit}</span>
                  {c.estimated_savings > 0 && <span className="text-sm font-bold text-amber-600">{fmt(c.estimated_savings)}</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{c.explanation}</p>
                {c.action && (
                  <div className="flex items-start gap-1.5 mt-2">
                    <ArrowRight className="w-3 h-3 text-violet-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-violet-600 dark:text-violet-400 font-medium">{c.action}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Filing Optimization */}
      {filing_optimization?.length > 0 && (
        <Section id="filing" title="Filing Optimization" icon={Target} iconColor="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400" count={filing_optimization.length}>
          <div className="space-y-3">
            {filing_optimization.map((f, i) => (
              <div key={i} className="p-3 rounded-lg border border-border">
                <p className="text-sm font-semibold text-foreground">{f.suggestion}</p>
                <p className="text-xs text-muted-foreground mt-1">{f.explanation}</p>
                {f.potential_impact && (
                  <Badge variant="outline" className="text-[10px] mt-2 bg-indigo-50 text-indigo-700 border-indigo-200">{f.potential_impact}</Badge>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Playbook Strategies */}
      {applicableStrategies.length > 0 && (
        <Section id="strategies" title="Tax Playbook Strategies" icon={BookOpen} iconColor="bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400" count={applicableStrategies.length}>
          <div className="space-y-2">
            {applicableStrategies.map((s, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-border">
                <div className={cn("w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold flex-shrink-0", priorityColor(s.priority))}>
                  P{s.priority}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-foreground">{s.strategy_name}</span>
                    {s.estimated_savings > 0 && <span className="text-sm font-bold text-emerald-600 flex-shrink-0">{fmt(s.estimated_savings)}</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{s.rationale}</p>
                  {s.already_used && <Badge variant="outline" className="text-[9px] mt-1.5 bg-emerald-50 text-emerald-600 border-emerald-200">Already Used</Badge>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Tax Plan */}
      {tax_plan?.actions?.length > 0 && (
        <Section id="plan" title={tax_plan.title || 'Year-Ahead Tax Plan'} icon={Map} iconColor="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400" count={tax_plan.actions.length}>
          {totalPlanSavings > 0 && (
            <div className="mb-4 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-center">
              <p className="text-xs text-emerald-600 font-medium">Estimated Total Savings</p>
              <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400 font-mono">{fmt(totalPlanSavings)}</p>
            </div>
          )}
          <div className="space-y-2">
            {['Q1', 'Q2', 'Q3', 'Q4', 'Ongoing'].map(quarter => {
              const items = tax_plan.actions.filter(a => a.quarter === quarter);
              if (items.length === 0) return null;
              return (
                <div key={quarter} className="space-y-1.5">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mt-2">{quarter}</p>
                  {items.map((a, i) => (
                    <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg bg-muted/70 border border-border">
                      <div className="flex-1">
                        <p className="text-xs font-medium text-foreground">{a.action}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-[9px] capitalize">{a.category}</Badge>
                          {a.estimated_impact > 0 && <span className="text-[10px] font-mono text-emerald-600">{fmt(a.estimated_impact)}</span>}
                          {a.deadline && <span className="text-[10px] text-muted-foreground">{a.deadline}</span>}
                          <Badge variant="outline" className={cn("text-[9px]", a.complexity === 'simple' ? 'text-emerald-600' : a.complexity === 'complex' ? 'text-rose-600' : 'text-amber-600')}>{a.complexity}</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Red Flags */}
      {red_flags?.length > 0 && (
        <Section id="flags" title="Red Flags & Concerns" icon={AlertTriangle} iconColor="bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400" count={red_flags.length}>
          <div className="space-y-2">
            {red_flags.map((f, i) => (
              <div key={i} className={cn("p-3 rounded-lg border", severityColor(f.severity))}>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="text-sm font-semibold">{f.issue}</span>
                  <Badge variant="outline" className="text-[9px] ml-auto capitalize">{f.severity}</Badge>
                </div>
                <p className="text-xs mt-1 opacity-80">{f.recommendation}</p>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
export { TaxReturnResults, TaxAnalysisReport };
